export interface RecordedEvent {
  type: string;
  timestamp: number;
  target?: {
    selector: string;
    tagName: string;
    id?: string;
    className?: string;
  };
  data?: any;
}

export class EventRecorder {
  private events: RecordedEvent[] = [];
  private startTime: number = 0;
  private isRecording: boolean = false;

  start(): void {
    this.events = [];
    this.startTime = Date.now();
    this.isRecording = true;
    console.log('Recording started at:', new Date(this.startTime).toISOString());
  }

  stop(): RecordedEvent[] {
    this.isRecording = false;
    return this.events;
  }

  addEvent(event: RecordedEvent): void {
    if (this.isRecording) {
      this.events.push(event);
    }
  }
  
  getElapsedTime(): number {
    if (!this.isRecording || !this.startTime) return 0;
    return Date.now() - this.startTime;
  }

  getEvents(): RecordedEvent[] {
    return [...this.events];
  }

  static getInjectionScript(): string {
    return `
    (function() {
      const events = [];
      const startTime = Date.now();
      
      // Helper to generate CSS selector for element
      function getSelector(element) {
        if (element.id) return '#' + element.id;
        
        let path = [];
        while (element && element.nodeType === Node.ELEMENT_NODE) {
          let selector = element.nodeName.toLowerCase();
          if (element.className) {
            selector += '.' + element.className.split(' ').join('.');
          }
          let sibling = element;
          let nth = 1;
          while (sibling.previousElementSibling) {
            sibling = sibling.previousElementSibling;
            if (sibling.nodeName === element.nodeName) nth++;
          }
          if (nth > 1) selector += ':nth-of-type(' + nth + ')';
          path.unshift(selector);
          element = element.parentNode;
        }
        return path.join(' > ');
      }

      // Record click events
      document.addEventListener('click', (e) => {
        const event = {
          type: 'click',
          timestamp: Date.now() - startTime,
          target: {
            selector: getSelector(e.target),
            tagName: e.target.tagName,
            id: e.target.id,
            className: e.target.className
          },
          data: {
            x: e.clientX,
            y: e.clientY,
            button: e.button
          }
        };
        events.push(event);
        window.parent.postMessage({ type: 'playground-event', event }, '*');
      }, true);

      // Record input events
      document.addEventListener('input', (e) => {
        const event = {
          type: 'input',
          timestamp: Date.now() - startTime,
          target: {
            selector: getSelector(e.target),
            tagName: e.target.tagName,
            id: e.target.id,
            className: e.target.className
          },
          data: {
            value: e.target.value,
            inputType: e.inputType
          }
        };
        events.push(event);
        window.parent.postMessage({ type: 'playground-event', event }, '*');
      }, true);

      // Record key events
      document.addEventListener('keydown', (e) => {
        const event = {
          type: 'keydown',
          timestamp: Date.now() - startTime,
          target: {
            selector: getSelector(e.target),
            tagName: e.target.tagName,
            id: e.target.id,
            className: e.target.className
          },
          data: {
            key: e.key,
            code: e.code,
            ctrlKey: e.ctrlKey,
            shiftKey: e.shiftKey,
            altKey: e.altKey,
            metaKey: e.metaKey
          }
        };
        events.push(event);
        window.parent.postMessage({ type: 'playground-event', event }, '*');
      }, true);

      // Record scroll events (throttled)
      let scrollTimeout;
      document.addEventListener('scroll', (e) => {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          const event = {
            type: 'scroll',
            timestamp: Date.now() - startTime,
            target: {
              selector: e.target === document ? 'document' : getSelector(e.target),
              tagName: e.target.tagName || 'document'
            },
            data: {
              scrollTop: e.target === document ? document.documentElement.scrollTop : e.target.scrollTop,
              scrollLeft: e.target === document ? document.documentElement.scrollLeft : e.target.scrollLeft
            }
          };
          events.push(event);
          window.parent.postMessage({ type: 'playground-event', event }, '*');
        }, 100);
      }, true);

      // Intercept navigation
      const originalPushState = history.pushState;
      history.pushState = function() {
        const result = originalPushState.apply(history, arguments);
        const event = {
          type: 'navigation',
          timestamp: Date.now() - startTime,
          data: {
            url: window.location.href,
            state: arguments[0]
          }
        };
        events.push(event);
        window.parent.postMessage({ type: 'playground-event', event }, '*');
        return result;
      };

      // Expose API for parent window
      window.getRecordedEvents = () => events;
      window.parent.postMessage({ type: 'recorder-ready' }, '*');
    })();
    `;
  }
}